import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useBrandData } from './useBrandData';

interface BrandThemeWrapperProps {
  brandId: string;
  children: React.ReactNode;
  contentType: 'landing-page' | 'blog-post' | 'social-post' | 'ad-group';
}

// Theme variable info type
export interface ThemeVariable {
  name: string;
  value: string;
  appliedValue: string;
  isApplied: boolean;
}

// Categories of theme variables
export interface ThemeVariables {
  colors: ThemeVariable[];
  typography: ThemeVariable[];
  spacing: ThemeVariable[];
  borders: ThemeVariable[];
  other: ThemeVariable[];
}

/**
 * Hook to manage brand theme variables and CSS
 */
export function useBrandTheme(brandId: string) {
  const [themeVariables, setThemeVariables] = useState<ThemeVariables>({
    colors: [],
    typography: [],
    spacing: [],
    borders: [],
    other: []
  });
  const [brandCss, setBrandCss] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Extract CSS variables from a CSS string
  const extractCssVariables = useCallback((css: string | null): Record<string, string> => {
    if (!css) return {};
    
    const variables: Record<string, string> = {};
    // Match all CSS variables, not just --brand-prefixed ones
    const varRegex = /--([\w-]+)\s*:\s*([^;!]+)/g;
    let match;
    
    while ((match = varRegex.exec(css)) !== null) {
      const name = `--${match[1]}`;
      const value = match[2].trim();
      variables[name] = value;
    }
    
    return variables;
  }, []);
  
  // Get the computed value of a CSS variable
  const getComputedValue = useCallback((variableName: string): string => {
    if (typeof window === 'undefined') return 'N/A';
    
    // First try to get from the brand wrapper element if available
    const brandElement = document.querySelector(`[data-brand-id="${brandId}"]`) as HTMLElement;
    if (brandElement) {
      const value = getComputedStyle(brandElement).getPropertyValue(variableName.replace('--', '')).trim();
      if (value) return value;
    }
    
    // Fallback to document root if not found in brand wrapper
    return getComputedStyle(document.documentElement).getPropertyValue(variableName.replace('--', '')).trim() || 'Not defined';
  }, [brandId]);
  
  // Categorize CSS variables for display
  const categorizeVariables = useCallback((extractedVars: Record<string, string>): ThemeVariables => {
    const result: ThemeVariables = {
      colors: [],
      typography: [],
      spacing: [],
      borders: [],
      other: []
    };
    
    for (const [name, value] of Object.entries(extractedVars)) {
      const appliedValue = getComputedValue(name);
      
      // Check if the variable is applied correctly within the brand wrapper
      const brandElement = document.querySelector(`[data-brand-id="${brandId}"]`) as HTMLElement;
      let isApplied = false;
      
      if (brandElement && appliedValue !== 'Not defined') {
        // Check if the applied value matches the expected value
        isApplied = appliedValue === value.trim() || 
                   appliedValue.replace(/\s+/g, '') === value.replace(/\s+/g, '');
      }
      
      const variable: ThemeVariable = {
        name, 
        value, 
        appliedValue, 
        isApplied
      };
      
      // Categorize variables
      if (name.includes('color') || name.includes('-primary') || name.includes('-secondary') || 
          name.includes('-accent') || name.includes('-success') || name.includes('-warning') || 
          name.includes('-error') || name.includes('-bg-')) {
        result.colors.push(variable);
      } else if (name.includes('font') || name.includes('line-height') || name.includes('letter-spacing')) {
        result.typography.push(variable);
      } else if (name.includes('spacing') || name.includes('padding') || name.includes('margin')) {
        result.spacing.push(variable);
      } else if (name.includes('border') || name.includes('radius')) {
        result.borders.push(variable);
      } else {
        result.other.push(variable);
      }
    }
    
    return result;
  }, [getComputedValue, brandId]);
  
  // Fetch and process brand CSS
  const fetchBrandCss = useCallback(async (brandId: string) => {
    setIsLoading(true);
    try {
      // Import supabase client
      const { supabase } = await import('@/lib/supabase');
      
      const { data, error } = await supabase
        .from('brands')
        .select('brand_css')
        .eq('id', brandId)
        .single();
      
      if (error) {
        console.error('Error fetching brand CSS:', error);
        return;
      }
      
      if (data?.brand_css) {
        console.log('Fetched brand CSS:', brandId);
        setBrandCss(data.brand_css);
        
        // Extract and categorize variables
        const extractedVars = extractCssVariables(data.brand_css);
        const categorized = categorizeVariables(extractedVars);
        setThemeVariables(categorized);
      }
    } catch (error) {
      console.error('Error in fetchBrandCss:', error);
    } finally {
      setIsLoading(false);
    }
  }, [extractCssVariables, categorizeVariables]);
  
  // Refresh CSS variables and their applied values
  const refreshThemeVariables = useCallback(() => {
    if (brandCss) {
      // First trigger the CSS reapplication for this specific brand
      window.dispatchEvent(new CustomEvent('refreshBrandTheme', { 
        detail: { brandId } 
      }));
      
      // Give time for styles to be applied to the scoped container
      setTimeout(() => {
        // Then update our state with the new values
        const extractedVars = extractCssVariables(brandCss);
        const categorized = categorizeVariables(extractedVars);
        setThemeVariables(categorized);
        
        console.log('Refreshed scoped theme variables for brand:', brandId);
      }, 100);
    }
  }, [brandId, brandCss, extractCssVariables, categorizeVariables]);
  
  return { 
    themeVariables, 
    brandCss, 
    isLoading, 
    fetchBrandCss, 
    refreshThemeVariables,
    getComputedValue
  };
}

/**
 * Theme Debug Panel Component
 */
export function ThemeDebugPanel({ brandId }: { brandId: string }) {
  const [showCss, setShowCss] = useState(false);
  const { 
    themeVariables, 
    brandCss, 
    isLoading, 
    fetchBrandCss, 
    refreshThemeVariables,
    getComputedValue 
  } = useBrandTheme(brandId);
  
  // Fetch brand CSS when debug panel is opened
  useEffect(() => {
    fetchBrandCss(brandId);
  }, [brandId, fetchBrandCss]);
  
  if (typeof window === 'undefined') return null;
  
  return (
    <div className="fixed top-0 right-0 bg-black/80 text-white p-4 m-2 z-50 max-w-md max-h-[80vh] overflow-auto text-xs rounded">
      <h3 className="text-sm font-bold mb-2 flex justify-between items-center">
        <span>Theme Variables</span>
        <div className="flex space-x-2">
          <button 
            onClick={() => {
              refreshThemeVariables();
              // Re-fetch after a short delay to show updated values
              setTimeout(() => {
                fetchBrandCss(brandId);
              }, 300);
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
          >
            Refresh CSS
          </button>
          <button 
            onClick={() => setShowCss(!showCss)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
          >
            {isLoading ? 'Loading...' : showCss ? 'Hide CSS' : 'Show Brand CSS'}
          </button>
        </div>
      </h3>
      
      {showCss ? (
        <div className="mt-2">
          <h4 className="font-semibold mb-1">Brand CSS:</h4>
          <pre className="bg-black/50 p-2 rounded overflow-auto max-h-[60vh] whitespace-pre-wrap">
            {brandCss || 'No CSS defined for this brand.'}
          </pre>
          
          {Object.values(themeVariables).some(arr => arr.length > 0) && (
            <div className="mt-4">
              <h4 className="font-semibold mb-1">Extracted Variables:</h4>
              
              {themeVariables.colors.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-blue-300 font-medium mb-1">Brand Colors:</h5>
                  {themeVariables.colors.map((variable) => (
                    <div key={variable.name} className="mb-1 flex items-center">
                      <span className="w-4 h-4 mr-2 rounded" style={{ backgroundColor: variable.value.includes('#') ? variable.value : '' }}></span>
                      <span className="text-green-400">{variable.name}</span>: <span>{variable.value}</span>
                      {!variable.isApplied && variable.appliedValue && (
                        <span className="ml-2 text-yellow-300 text-xs">
                          (Using: {variable.appliedValue})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {themeVariables.typography.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-blue-300 font-medium mb-1">Typography:</h5>
                  {themeVariables.typography.map((variable) => (
                    <div key={variable.name} className="mb-1">
                      <span className="text-green-400">{variable.name}</span>: <span>{variable.value}</span>
                      {!variable.isApplied && variable.appliedValue && (
                        <span className="ml-2 text-yellow-300 text-xs">
                          (Using: {variable.appliedValue})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {themeVariables.spacing.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-blue-300 font-medium mb-1">Spacing:</h5>
                  {themeVariables.spacing.map((variable) => (
                    <div key={variable.name} className="mb-1">
                      <span className="text-green-400">{variable.name}</span>: <span>{variable.value}</span>
                      {!variable.isApplied && variable.appliedValue && (
                        <span className="ml-2 text-yellow-300 text-xs">
                          (Using: {variable.appliedValue})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {themeVariables.borders.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-blue-300 font-medium mb-1">Borders & Radius:</h5>
                  {themeVariables.borders.map((variable) => (
                    <div key={variable.name} className="mb-1">
                      <span className="text-green-400">{variable.name}</span>: <span>{variable.value}</span>
                      {!variable.isApplied && variable.appliedValue && (
                        <span className="ml-2 text-yellow-300 text-xs">
                          (Using: {variable.appliedValue})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {themeVariables.other.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-blue-300 font-medium mb-1">Other Variables:</h5>
                  {themeVariables.other.map((variable) => (
                    <div key={variable.name} className="mb-1">
                      <span className="text-green-400">{variable.name}</span>: <span>{variable.value}</span>
                      {!variable.isApplied && variable.appliedValue && (
                        <span className="ml-2 text-yellow-300 text-xs">
                          (Using: {variable.appliedValue})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getComputedValue('--brand-primary') || 'var(--brand-primary)' }}></div>
            <div>--brand-primary: {getComputedValue('--brand-primary')}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getComputedValue('--brand-secondary') || 'var(--brand-secondary)' }}></div>
            <div>--brand-secondary: {getComputedValue('--brand-secondary')}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getComputedValue('--brand-accent') || 'var(--brand-accent)' }}></div>
            <div>--brand-accent: {getComputedValue('--brand-accent')}</div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/20">
            <h5 className="font-medium mb-2">Typography:</h5>
            <div className="grid grid-cols-1 gap-1">
              <div>--font-heading: <span style={{ fontFamily: getComputedValue('--font-heading') || 'var(--font-heading, inherit)' }}>Sample Text</span> {getComputedValue('--font-heading')}</div>
              <div>--font-body: <span style={{ fontFamily: getComputedValue('--font-body') || 'var(--font-body, inherit)' }}>Sample Text</span> {getComputedValue('--font-body')}</div>
              <div>--brand-font-primary: <span style={{ fontFamily: getComputedValue('--brand-font-primary') || 'var(--brand-font-primary, inherit)' }}>Sample Text</span> {getComputedValue('--brand-font-primary')}</div>
              <div>--font-weight-normal: {getComputedValue('--font-weight-normal')}</div>
              <div>--font-weight-bold: {getComputedValue('--font-weight-bold')}</div>
              <div>--line-height: {getComputedValue('--line-height')}</div>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/20">
            <h5 className="font-medium mb-2">Layout & Spacing:</h5>
            <div className="grid grid-cols-1 gap-1">
              <div>--border-radius-sm: {getComputedValue('--border-radius-sm')}</div>
              <div>--border-radius-md: {getComputedValue('--border-radius-md')}</div>
              <div>--border-radius-lg: {getComputedValue('--border-radius-lg')}</div>
              <div>--border-radius-full: {getComputedValue('--border-radius-full')}</div>
              <div>--spacing-xs: {getComputedValue('--spacing-xs')}</div>
              <div>--spacing-sm: {getComputedValue('--spacing-sm')}</div>
              <div>--spacing-md: {getComputedValue('--spacing-md')}</div>
              <div>--spacing-lg: {getComputedValue('--spacing-lg')}</div>
              <div>--spacing-xl: {getComputedValue('--spacing-xl')}</div>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="text-xs text-gray-400">Brand ID: {brandId}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export const BrandThemeWrapper: React.FC<BrandThemeWrapperProps> = ({ 
  brandId, 
  children,
  contentType
}) => {
  const { brand, fetchBrandDetails } = useBrandData(brandId);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stylesApplied, setStylesApplied] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  
  // Check URL for debug parameter
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setShowDebug(urlParams.get('debug') === 'theme');
    }
  }, []);
  
  // Fetch brand details when brandId changes
  useEffect(() => {
    if (brandId) {
      fetchBrandDetails(brandId);
    }
  }, [brandId, fetchBrandDetails]);
  
  /**
   * Directly inject the entire brand CSS into the document with specific IDs
   * This centralizes all CSS handling in one place without breaking it apart
   * SCOPED TO WRAPPER CONTAINER ONLY - does not affect global styles
   */
  const injectStyles = useCallback((css: string) => {
    if (!css) return;
    
    console.log(`Injecting scoped styles for brand ${brandId}...`);
    
    // Create unique selector for this brand wrapper
    const brandSelector = `[data-brand-id="${brandId}"]`;
    
    // Step 1: Extract CSS variables and apply them only to the wrapper container
    const extractAndApplyVariables = (css: string) => {
      const varRegex = /--([\w-]+)\s*:\s*([^;!]+)/g;
      let match;
      const extractedVars: Record<string, string> = {};
      
      while ((match = varRegex.exec(css)) !== null) {
        const name = `--${match[1]}`;
        const value = match[2].trim();
        extractedVars[name] = value;
        
        // Apply ONLY to the wrapper container, not to document root
        if (containerRef.current) {
          containerRef.current.style.setProperty(name, value, 'important');
        }
      }
      
      console.log('Applied CSS variables to wrapper container only:', extractedVars);
      return extractedVars;
    };
    
    // Apply variables to wrapper container only
    const extractedVars = extractAndApplyVariables(css);
    
    // Create unique IDs for this brand
    const styleId = `brand-style-${brandId}`;
    
    // Remove any existing styles for this brand
    const existingStyle = document.getElementById(styleId);
    if (existingStyle && existingStyle.parentNode) {
      existingStyle.parentNode.removeChild(existingStyle);
    }
    
    // Create new style element with scoped styles
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.setAttribute('data-brand-id', brandId);
    styleEl.setAttribute('data-content-type', contentType);
    
    // Fix any missing Google Fonts
    const fontFamilies = [];
    
    // Check for font families used but not imported
    if (css.includes("'Montserrat'") || css.includes('"Montserrat"')) {
      fontFamilies.push('Montserrat:wght@400;700');
    }
    if (css.includes("'Open Sans'") || css.includes('"Open Sans"')) {
      fontFamilies.push('Open+Sans:wght@400;600;700');
    }
    if (css.includes("'Roboto'") || css.includes('"Roboto"')) {
      fontFamilies.push('Roboto:wght@400;500;700');
    }
    if (css.includes("'Poppins'") || css.includes('"Poppins"')) {
      fontFamilies.push('Poppins:wght@400;500;600;700');
    }
    
    // Start building scoped CSS
    let finalCSS = '';
    
    // Add Google Fonts import if needed (this can be global)
    if (fontFamilies.length > 0 && !css.includes('@import url')) {
      const fontImport = `@import url('https://fonts.googleapis.com/css2?family=${fontFamilies.join('&family=')}&display=swap');\n`;
      finalCSS = fontImport;
      console.log('Added Google Fonts import:', fontImport.trim());
    }
    
    // Transform CSS to be scoped to the wrapper
    let scopedCSS = css;
    
    // Replace :root with our brand selector
    scopedCSS = scopedCSS.replace(/:root\s*{/g, `${brandSelector} {`);
    
    // If there's no :root selector, create one with our extracted variables
    if (!css.includes(':root')) {
      const rootVars = Object.entries(extractedVars)
        .map(([name, value]) => `  ${name}: ${value} !important;`)
        .join('\n');
      
      if (rootVars) {
        const scopedRootBlock = `${brandSelector} {\n${rootVars}\n}\n\n`;
        scopedCSS = scopedRootBlock + scopedCSS;
      }
    }
    
    // Scope any other top-level selectors to be within our wrapper
    const lines = scopedCSS.split('\n');
    let insideBraces = 0;
    let processedLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Count braces to track nesting
      const openBraces = (line.match(/{/g) || []).length;
      const closeBraces = (line.match(/}/g) || []).length;
      
      // If we're at the top level and this looks like a CSS selector (not @import, @media, etc.)
      if (insideBraces === 0 && 
          line.trim() && 
          !line.startsWith('@') && 
          !line.includes('/*') && 
          !line.includes('*/') &&
          line.includes('{') && 
          !line.includes(brandSelector)) {
        
        // Scope the selector
        const selectorPart = line.substring(0, line.indexOf('{'));
        const restOfLine = line.substring(line.indexOf('{'));
        
        // Add our brand selector as a parent
        processedLines.push(`${brandSelector} ${selectorPart.trim()} ${restOfLine}`);
      } else {
        processedLines.push(line);
      }
      
      insideBraces += openBraces - closeBraces;
    }
    
    finalCSS += processedLines.join('\n');
    
    // Add the scoped styles to the document
    styleEl.textContent = finalCSS;
    
    // Insert the style element
    document.head.appendChild(styleEl);
    
    // Create unique class for the container
    if (containerRef.current) {
      const brandClass = `brand-${brandId}`;
      const contentTypeClass = `content-type-${contentType}`;
      
      containerRef.current.classList.add(brandClass, contentTypeClass);
    }
    
    console.log(`Scoped styles applied for brand ${brandId} (${contentType})`);
    
    // Add utility classes for components to use (scoped to container)
    const { primary, secondary, accent } = extractColors(css);
    if (containerRef.current) {
      containerRef.current.style.setProperty('--current-brand-primary', primary || '#3b82f6');
      containerRef.current.style.setProperty('--current-brand-secondary', secondary || '#10b981');
      containerRef.current.style.setProperty('--current-brand-accent', accent || '#f97316');
    }
    
    return styleEl;
  }, [brandId, contentType]);
  
  /**
   * Extract brand colors for utility purposes
   */
  const extractColors = (css: string) => {
    const colors = {
      primary: '',
      secondary: '',
      accent: ''
    };
    
    // Extract brand colors from CSS
    const primaryMatch = css.match(/--brand-primary\s*:\s*([^;!]+)/);
    const secondaryMatch = css.match(/--brand-secondary\s*:\s*([^;!]+)/);
    const accentMatch = css.match(/--brand-accent\s*:\s*([^;!]+)/);
    
    if (primaryMatch) colors.primary = primaryMatch[1].trim();
    if (secondaryMatch) colors.secondary = secondaryMatch[1].trim();
    if (accentMatch) colors.accent = accentMatch[1].trim();
    
    return colors;
  };
  
  /**
   * Apply the brand's CSS when available
   */
  useEffect(() => {
    if (!brand?.brand_css) {
      return;
    }
    
    // Inject the styles
    const styleEl = injectStyles(brand.brand_css);
    setStylesApplied(true);
    
    // Clean up function
    return () => {
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
      setStylesApplied(false);
    };
  }, [brand, injectStyles]);
  
  /**
   * Handle refresh events from debug panel
   */
  useEffect(() => {
    const handleRefreshEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.brandId === brandId) {
        console.log('Received refresh event for brand ID:', brandId);
        
        // Force re-inject of styles
        if (brand?.brand_css) {
          injectStyles(brand.brand_css);
          setStylesApplied(true);
        }
      }
    };
    
    window.addEventListener('refreshBrandTheme', handleRefreshEvent);
    
    return () => {
      window.removeEventListener('refreshBrandTheme', handleRefreshEvent);
    };
  }, [brandId, brand, injectStyles]);
  
  /**
   * Debug info for the current brand styles
   */
  useEffect(() => {
    if (stylesApplied) {
      // Log current state of CSS variables
      if (typeof window !== 'undefined') {
        console.log('Current CSS variables:');
        ['--brand-primary', '--brand-secondary', '--brand-accent', 
         '--font-heading', '--font-body', '--border-radius-md'].forEach(varName => {
          console.log(`${varName}:`, 
            getComputedStyle(document.documentElement).getPropertyValue(varName.replace('--', '')));
        });
      }
    }
  }, [stylesApplied]);
  
  return (
    <div 
      ref={containerRef} 
      className={`brand-wrapper content-type-${contentType}`} 
      data-brand-id={brandId}
      data-styles-applied={stylesApplied ? 'true' : 'false'}
    >
      {showDebug && <ThemeDebugPanel brandId={brandId} />}
      {children}
    </div>
  );
};

export default BrandThemeWrapper;

/**
 * Global function to force refresh brand CSS
 * Can be called from dev tools console for debugging
 * Now safely scoped to only affect brand wrappers
 */
export function refreshBrandStyles(brandId?: string) {
  if (brandId) {
    console.log(`Manually refreshing scoped brand styles for ${brandId}`);
    window.dispatchEvent(new CustomEvent('refreshBrandTheme', { 
      detail: { brandId } 
    }));
    
    // Force recalculation only on the specific brand wrapper
    const brandWrapper = document.querySelector(`[data-brand-id="${brandId}"]`) as HTMLElement;
    if (brandWrapper) {
      const tempClass = `force-recalc-${Date.now()}`;
      brandWrapper.classList.add(tempClass);
      
      // Force reflow on just this element
      // eslint-disable-next-line no-unused-expressions
      brandWrapper.offsetHeight;
      
      // Remove class
      brandWrapper.classList.remove(tempClass);
      console.log(`Style recalculation forced for brand wrapper: ${brandId}`);
    }
  } else {
    // Find all brand style elements and refresh them all
    const brandStyleElements = document.querySelectorAll('[data-brand-id]');
    console.log(`Manually refreshing all ${brandStyleElements.length} scoped brand styles`);
    
    brandStyleElements.forEach(elem => {
      const id = elem.getAttribute('data-brand-id');
      if (id) {
        window.dispatchEvent(new CustomEvent('refreshBrandTheme', { 
          detail: { brandId: id } 
        }));
        
        // Force recalculation only on this specific wrapper
        const tempClass = `force-recalc-${Date.now()}`;
        elem.classList.add(tempClass);
        
        // Force reflow on just this element
        // eslint-disable-next-line no-unused-expressions
        (elem as HTMLElement).offsetHeight;
        
        // Remove class
        elem.classList.remove(tempClass);
      }
    });
    
    console.log('All brand wrapper styles refreshed');
  }
  
  return 'Brand styles refreshed (scoped)';
}

// Make it globally available in dev mode
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).refreshBrandStyles = refreshBrandStyles;
}